// Library Methods
import { useTranslation } from "react-i18next";

// Components
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { PrintIcon } from "src/assets/icons";

// Constants
import { ReportStatus } from "src/constants/enums";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { FileInfoType } from "src/types/sharedTypes";

type PrintButtonType = {
  isLoading: boolean;
  fileInfo: FileInfoType;
  reportInfoError: boolean;
  showDownloadButtons: boolean;
  printFile: () => Promise<void>;
};

const PrintButton = ({
  isLoading,
  fileInfo,
  reportInfoError,
  showDownloadButtons,
  printFile,
}: PrintButtonType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  // Derived directly from fileInfo on every render -- no local state to get
  // stuck, since the report's status is always the current source of truth.
  const isPendingReport =
    !fileInfo?.reportInfoData ||
    fileInfo.reportInfoData.status === ReportStatus.Pending ||
    fileInfo.reportInfoData.status === ReportStatus.InProgress;

  const isDisabled =
    reportInfoError || fileInfo?.reportInfoData?.status !== ReportStatus.Ready;

  if (isLoading) {
    return <Skeleton variant="circular" width={24} height={24} />;
  }

  if (!isLoading && !showDownloadButtons) {
    return null;
  }

  return (
    <Tooltip
      title={
        isPendingReport
          ? t("Creating {{ fileName }}", { fileName: "PDF" })
          : t("Print PDF")
      }
    >
      {/* wrapping span lets the tooltip fire even while the button is disabled */}
      <span style={{ display: "flex" }}>
        <IconButton
          aria-label={t("Print PDF")}
          disabled={isDisabled || fileInfo?.isPrinting}
          onClick={(event) => {
            // Stop the click from bubbling up to an ancestor Link (this button
            // is rendered inline inside Breadcrumb's navigational link) --
            // otherwise printing would also navigate away from the page.
            event.preventDefault();
            event.stopPropagation();
            printFile();
          }}
          size="small"
          sx={{ color: onSurface }}
        >
          {isPendingReport || fileInfo?.isPrinting ? (
            <CircularProgress
              size={24}
              sx={{ color: "inherit" }}
              disableShrink
            />
          ) : (
            <PrintIcon />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default PrintButton;
